/**
 * 权限树工具函数
 * 用于将 PermissionTransferItem[] 转换为 GenericPanelTree 所需的 TreeNodeData[]
 */
import React from "react";
import { PermissionTransferItem } from "../config/types";
import type { PermissionTreeNodeData } from "@ms/shared-components/components/GenericPanelTree";
import { FolderOutlined, FileOutlined } from "@ant-design/icons";
import { Space, Typography, Tooltip } from "antd";

const { Text } = Typography;

/** 避免 title/code 等为对象时在界面出现 [object Object] */
function safeText(v: unknown, fallback = ""): string {
  if (v == null || v === "") return fallback;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (typeof o.code === "string") return o.code;
    if (typeof o.name === "string") return o.name;
    if (typeof o.title === "string") return o.title;
  }
  return fallback;
}

/**
 * 将 PermissionTransferItem[] 转换为 TreeNodeData[]
 */
export const convertPermissionTreeToTreeNodeData = (
  permissionTree: PermissionTransferItem[],
  disabledKeys: string[] = [],
  rolePermissionSources: Record<string, string[]> = {}
): PermissionTreeNodeData[] => {
  if (!permissionTree || permissionTree.length === 0) {
    return [];
  }

  const convertToTreeNode = (permission: PermissionTransferItem, level: number = 1): PermissionTreeNodeData => {
    const isModule = permission.type === "MODULE";
    const isDisabled = disabledKeys.includes(permission.id);
    const sources = rolePermissionSources[permission.id] || [];

    // 根据层级设置不同的样式和显示文本
    let titleStyle: React.CSSProperties = {};
    let iconColor = "";
    let displayText = "";

    if (level === 1) {
      // 第一层：微服务 - 显示"mshotel 酒店服务"（先英文服务代码，后中文名称）
      titleStyle = { fontWeight: 600, fontSize: "14px" };
      iconColor = "#1890ff";
      // 英文服务代码（优先 permission.permissionData.serviceName；否则从子节点的 code 推断）
      let serviceCode = safeText(permission.permissionData?.serviceName, "");
      if (!serviceCode && Array.isArray(permission.children) && permission.children.length > 0) {
        const fn = permission.children.find((c) => {
          const cc = safeText(c.permissionData?.code ?? c.code, "");
          return cc.includes(":");
        });
        if (fn) {
          const cc = safeText(fn.permissionData?.code ?? fn.code, "");
          serviceCode = cc.split(":")[0] || "";
        }
      }
      // 中文服务名（从标题去掉“服务”后缀）
      const titleRaw = safeText(permission.title, "");
      const serviceCn = (titleRaw.replace(/服务$/, "") || "未知服务") as string;
      // 先代码，后中文
      displayText = `${serviceCode ? serviceCode + " " : ""}${serviceCn}`;
    } else if (level === 2) {
      // 第二层：页面 - 仅显示"service:page"（不带中文）
      titleStyle = { fontWeight: 500, fontSize: "13px" };
      iconColor = "#52c41a";
      // 优先从 code 提取，保证为英文
      let code = safeText(
        permission.permissionData?.code ?? permission.code,
        ""
      );
      let part0 = "";
      let part1 = "";
      if (typeof code === "string" && code.includes(":")) {
        const parts = code.split(":");
        part0 = parts[0] || "";
        part1 = parts[1] || "";
      }
      // 若本节点缺少 code，则尝试从子节点的完整 code 推断
      if ((!part0 || !part1) && Array.isArray(permission.children)) {
        const fn = permission.children.find((c) => {
          const cc = safeText(c.permissionData?.code ?? c.code, "");
          return cc.includes(":") && cc.split(":").length >= 2;
        });
        if (fn) {
          const cc = safeText(fn.permissionData?.code ?? fn.code, "");
          const parts = cc.split(":");
          part0 = parts[0] || part0;
          part1 = parts[1] || part1;
        }
      }
      displayText = [part0, part1].filter(Boolean).join(":");
      if (!displayText) {
        displayText = safeText(permission.title, "") || "—";
      }
    } else {
      // 第三层：操作 - 显示"xxx:yyy:zzz"（英文）
      titleStyle = { fontSize: "13px" };
      if (isDisabled) {
        titleStyle = { ...titleStyle, color: "#999" };
      }
      // 使用权限代码，格式为 service:page:action
      displayText =
        safeText(permission.permissionData?.code ?? permission.code, "") ||
        safeText(permission.title, "") ||
        "未知权限";
    }

    const titleContent = (
      <Space style={{ pointerEvents: "none" }}>
        {isModule ? (
          <FolderOutlined style={{ color: iconColor }} />
        ) : (
          <>
            {isDisabled ? (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                🔒
              </Text>
            ) : (
              <FileOutlined style={{ color: "#8c8c8c" }} />
            )}
          </>
        )}
        <Text style={titleStyle}>{displayText}</Text>
        {!isModule && isDisabled && sources.length > 0 && (
          <Text type="secondary" style={{ fontSize: "11px" }}>
            (来自角色)
          </Text>
        )}
      </Space>
    );

    const node: PermissionTreeNodeData = {
      key: permission.id,
      title:
        !isModule && isDisabled && sources.length > 0 ? (
          <Tooltip title={`此权限来自角色：${sources.join("、")}，不可取消`}>
            <span style={{ pointerEvents: "none" }}>{titleContent}</span>
          </Tooltip>
        ) : (
          titleContent
        ),
      children: permission.children?.map((child) => convertToTreeNode(child, level + 1)),
      isLeaf: !isModule,
      disabled: isDisabled,
      permission: permission.permissionData,
      serviceName: permission.permissionData?.serviceName,
      isAssigned: permission.isAssigned,
      type: permission.type as "MODULE" | "FUNCTION",
    };

    return node;
  };

  return permissionTree.map((node) => convertToTreeNode(node, 1));
};

/**
 * 从权限树中提取已分配的权限ID
 */
export const extractAssignedPermissionIds = (permissionTree: PermissionTransferItem[]): string[] => {
  const ids: string[] = [];
  permissionTree.forEach((permission) => {
    if (permission.isAssigned && permission.type !== "MODULE") {
      ids.push(permission.id);
    }
    if (permission.children) {
      ids.push(...extractAssignedPermissionIds(permission.children));
    }
  });
  return ids;
};

/**
 * 根据选中的权限ID更新权限树中的 isAssigned 属性
 */
export const updatePermissionTreeAssignedStatus = (
  permissionTree: PermissionTransferItem[],
  checkedKeys: string[]
): PermissionTransferItem[] => {
  const checkedSet = new Set(checkedKeys);

  const updateNode = (node: PermissionTransferItem): PermissionTransferItem => {
    const updatedNode = { ...node };

    // 只更新非 MODULE 类型的节点的 isAssigned 属性
    if (node.type !== "MODULE") {
      updatedNode.isAssigned = checkedSet.has(node.id);
    }

    // 递归更新子节点
    if (node.children && node.children.length > 0) {
      updatedNode.children = node.children.map(updateNode);
    }

    return updatedNode;
  };

  return permissionTree.map(updateNode);
};
